import { Injectable, inject } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, catchError, of, switchMap, throwError, EMPTY, merge } from 'rxjs';
import { IndexedDBService } from '@libs/shared-services';
import { patientChartDBKeys, routes, SKIP_CACHE } from '@libs/utils';
import { PatientChart } from '@ffd/api-client';
import { Store } from '@ngrx/store';

export interface PatientChartData {
  chart: PatientChart;
  createdAt: Date;
}

@Injectable({ providedIn: 'root' })
export class ProgressNotesInterceptorService implements HttpInterceptor {
  private readonly indexedDbService = inject(IndexedDBService);
  private readonly store = inject(Store);

  private db: IDBDatabase | undefined;

  constructor() {
    this.indexedDbService
      .dbOpen(patientChartDBKeys.dbName, patientChartDBKeys.version, patientChartDBKeys.objectStoreName)
      .subscribe((db) => {
        this.db = db;
      });
  }

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<PatientChart>> {
    const url = req.urlWithParams;
    const siteIdMatch = url.match(/sites\/([^/]+)/);
    const siteId = siteIdMatch ? siteIdMatch[1] : null;
    const patientIdMatch = url.match(/patients\/([^/]+)/);
    const patientId = patientIdMatch ? patientIdMatch[1] : null;
    const chartResponseId = `${patientId}_${siteId}`;
    if (req.url.includes('/chart') && req.method === 'GET' && this.db && chartResponseId && siteId !== routes.showcaseSiteId) {
      return req.context.get(SKIP_CACHE)
        ? this.handleRequestAndStoreInCache(chartResponseId, next, req)
        : merge(
            this.getCachedChart(chartResponseId).pipe(
              catchError(() => {
                return EMPTY;
              }),
            ),
            this.handleRequestAndStoreInCache(chartResponseId, next, req),
          );
    }
    return next.handle(req);
  }

  private readonly getCachedChart = (chartResponseId: string) => {
    const readOS = this.db!.transaction([patientChartDBKeys.objectStoreName], 'readonly').objectStore(patientChartDBKeys.objectStoreName);
    return this.indexedDbService.dbGet<PatientChartData>(readOS, chartResponseId).pipe(
      switchMap((data) => {
        if (data) {
          return of(
            new HttpResponse({
              body: data.chart,
              status: 200,
            }),
          );
        }
        return EMPTY;
      }),
    );
  };

  private readonly handleRequestAndStoreInCache = (
    chartResponseId: string,
    next: HttpHandler,
    req: HttpRequest<unknown>,
  ): Observable<HttpEvent<PatientChart>> => {
    return next.handle(req).pipe(
      catchError(() => {
        return throwError(() => 'Error fetching patient chart');
      }),
      switchMap((response) => {
        if (response instanceof HttpResponse) {
          // here we can dispatch an action to notify the store that the real data is fetched
          //this.store.dispatch();
          const writeOs = this.db!.transaction([patientChartDBKeys.objectStoreName], 'readwrite').objectStore(
            patientChartDBKeys.objectStoreName,
          );
          return this.indexedDbService
            .dbPut<PatientChartData>(writeOs, chartResponseId, {
              chart: response.body,
              createdAt: new Date(),
            })
            .pipe(
              switchMap(async () => {
                await this.indexedDbService.dbHouseKeeping(writeOs);
                return response;
              }),
            );
        }
        return of(response);
      }),
    );
  };
}
